import React, { useEffect, useRef, useState } from "react";

import Loading from "../../../component/Loading";
import { VISIT_PAGE } from "../../../config/lang";
import { FILTER_DEBOUNCE_MS } from "../../../config/config";

import { call } from "../../../util/action";
import { getStore } from "../../../util/redux/store";
import { translate } from "../../../util/util";

const me = getStore().getState().me;

const formatDate = dateStr => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const TAB_CONFIG = [
  { key: "products", icon: "inventory_2", labelKey: "surveys_tab_products" },
  { key: "brands", icon: "label", labelKey: "surveys_tab_brands" },
  { key: "categories", icon: "category", labelKey: "surveys_tab_categories" },
  { key: "other", icon: "help_outline", labelKey: "surveys_tab_other" },
];

const ANSWER_TYPE_META = {
  boolean: { icon: "toggle_on", labelKey: "surveys_answer_type_bool" },
  numeric: { icon: "tag", labelKey: "surveys_answer_type_numeric" },
  dropdown: { icon: "list", labelKey: "surveys_answer_type_dropdown" },
  text: { icon: "notes", labelKey: "surveys_answer_type_text" },
  date: { icon: "event", labelKey: "surveys_answer_type_date" },
  photo: { icon: "image", labelKey: "surveys_answer_type_photo" },
};

const renderAnswerCell = (answer, onOpenPhoto) => {
  if (!answer.question) return null;
  switch (answer.question.answer_type) {
    case "boolean":
      return (
        <span className={`visit-a-bool ${answer.answer_boolean ? "yes" : "no"}`}>
          <span className="material-symbols-outlined">{answer.answer_boolean ? "check_circle" : "cancel"}</span>
          {answer.answer_boolean ? translate(VISIT_PAGE, "surveys_answer_bool_yes", me.language) : translate(VISIT_PAGE, "surveys_answer_bool_no", me.language)}
        </span>
      );
    case "numeric":
      return <span className="visit-a-numeric">{answer.answer_numeric}</span>;
    case "dropdown":
      return <span className="visit-a-dropdown">{answer.answer_dropdown?.label}</span>;
    case "text":
      return <span className="visit-a-text">{answer.answer_text}</span>;
    case "date":
      return (
        <span className="visit-a-date">
          <span className="material-symbols-outlined">event</span>
          {answer.answer_date}
        </span>
      );
    case "photo": {
      const photos = answer.photos || [];
      return (
        <div className="visit-a-photos">
          {photos.map((photo, i) => (
            <div
              key={photo.id || i}
              className="visit-photo-mini"
              onClick={e => {
                e.stopPropagation();
                onOpenPhoto(photos, i, answer.question?.text);
              }}>
              {photo.photo ? <img src={photo.photo} alt="" /> : <span className="material-symbols-outlined">image</span>}
              <div className="visit-photo-mini-overlay">
                <span className="material-symbols-outlined">zoom_in</span>
              </div>
            </div>
          ))}
          <span className="visit-photo-label">
            {photos.length} {translate(VISIT_PAGE, "surveys_photo_label", me.language)}
          </span>
        </div>
      );
    }
    default:
      return null;
  }
};

const Surveys = ({ data }) => {
  const [state, setState] = useState({ summary: null, surveys: [], loading: false });
  const [expandedCards, setExpandedCards] = useState({});
  const [surveyDetails, setSurveyDetails] = useState({});
  const [surveyDetailLoading, setSurveyDetailLoading] = useState({});
  const [activeTabs, setActiveTabs] = useState({});
  const [collapsedEntities, setCollapsedEntities] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [entitySearchDebounced, setEntitySearchDebounced] = useState({});
  const entitySearchTimers = useRef({});
  const [entityLimit, setEntityLimit] = useState({});

  const ENTITY_PAGE = 5;
  const getLimit = (surveyId, groupKey) => entityLimit[`${surveyId}-${groupKey}`] || ENTITY_PAGE;
  const showMore = (surveyId, groupKey) =>
    setEntityLimit(prev => ({ ...prev, [`${surveyId}-${groupKey}`]: (prev[`${surveyId}-${groupKey}`] || ENTITY_PAGE) + ENTITY_PAGE }));
  const [photoModal, setPhotoModal] = useState(null);

  const handleEntitySearch = (surveyId, value) => {
    clearTimeout(entitySearchTimers.current[surveyId]);
    entitySearchTimers.current[surveyId] = setTimeout(() => {
      setEntitySearchDebounced(prev => ({ ...prev, [surveyId]: value }));
    }, FILTER_DEBOUNCE_MS);
  };

  const openPhotoModal = (photos, index, question) => setPhotoModal({ photos, index, question });
  const closePhotoModal = () => setPhotoModal(null);
  const prevPhoto = () => setPhotoModal(prev => ({ ...prev, index: prev.index - 1 }));
  const nextPhoto = () => setPhotoModal(prev => ({ ...prev, index: prev.index + 1 }));

  useEffect(() => {
    let isMounted = true;
    setState({ summary: null, surveys: [], loading: true });
    call({ method: "GET", url: `visits/${data.id}/surveys_in_visit/` }, response => {
      if (!isMounted) return;
      const surveys = (response.surveys || []).filter(s => (s.answers_count ?? 0) > 0 || (s.photos_count ?? 0) > 0);
      setState({ summary: response.summary, surveys, loading: false });
    });
    return () => {
      isMounted = false;
    };
  }, [data.id]);

  useEffect(() => {
    const timers = entitySearchTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const toggleCard = id => {
    const willExpand = !expandedCards[id];
    setExpandedCards(prev => ({ ...prev, [id]: willExpand }));

    if (willExpand && !surveyDetails[id]) {
      setSurveyDetailLoading(prev => ({ ...prev, [id]: true }));
      call({ method: "GET", url: `visit_surveys/${id}/?visit_id=${data.id}` }, response => {
        setSurveyDetails(prev => ({ ...prev, [id]: response }));
        setSurveyDetailLoading(prev => ({ ...prev, [id]: false }));
      });
    }
  };

  const switchTab = (surveyId, tab, e) => {
    e.stopPropagation();
    setActiveTabs(prev => ({ ...prev, [surveyId]: tab }));
  };
  const toggleEntity = (entityId, e) => {
    e.stopPropagation();
    setCollapsedEntities(prev => ({ ...prev, [entityId]: !prev[entityId] }));
  };

  const { summary, surveys, loading } = state;

  return (
    <div className="visit-surveys-tab-wrapper">
      {summary ? (
        <>
          <div className="visit-surveys-stats-row">
            <div className="visit-surveys-stat-card">
              <div className="visit-surveys-stat-icon visit-surveys-stat-icon--primary">
                <span className="material-symbols-outlined">quiz</span>
              </div>
              <div>
                <div className="visit-surveys-stat-value">{summary.surveys ?? "-"}</div>
                <div className="visit-surveys-stat-label">{translate(VISIT_PAGE, "surveys_summary_surveys_label", me.language)}</div>
              </div>
            </div>
            <div className="visit-surveys-stat-card">
              <div className="visit-surveys-stat-icon visit-surveys-stat-icon--secondary">
                <span className="material-symbols-outlined">check_circle</span>
              </div>
              <div>
                <div className="visit-surveys-stat-value">{summary.answers ?? "-"}</div>
                <div className="visit-surveys-stat-label">{translate(VISIT_PAGE, "surveys_summary_answers_label", me.language)}</div>
              </div>
            </div>
            <div className="visit-surveys-stat-card">
              <div className="visit-surveys-stat-icon visit-surveys-stat-icon--tertiary">
                <span className="material-symbols-outlined">photo_library</span>
              </div>
              <div>
                <div className="visit-surveys-stat-value">{summary.photos ?? "-"}</div>
                <div className="visit-surveys-stat-label">{translate(VISIT_PAGE, "surveys_summary_photos_label", me.language)}</div>
              </div>
            </div>
          </div>

          <div className="visit-filter-bar">
            <div className="visit-search-bar">
              <span className="material-symbols-outlined">search</span>
              <input
                type="text"
                placeholder={translate(VISIT_PAGE, "surveys_search_placeholder", me.language)}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            {searchQuery && (
              <button className="visit-btn-text" onClick={() => setSearchQuery("")}>
                {translate(VISIT_PAGE, "surveys_reset_btn", me.language)}
              </button>
            )}
          </div>

          <div className="visit-record-count">
            {surveys.filter(s => s.title.toLowerCase().includes(searchQuery.toLowerCase())).length}&nbsp;
            {translate(VISIT_PAGE, "surveys_summary_surveys_label", me.language)}&nbsp;•&nbsp;
            {summary.answers}&nbsp;{translate(VISIT_PAGE, "surveys_summary_answers_label", me.language)}
          </div>

          <div className="visit-survey-list">
            {(() => {
              const filteredSurveys = surveys.filter(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()));
              if (filteredSurveys.length === 0) {
                return (
                  <div className="visit-empty-state">
                    <span className="material-symbols-outlined">search_off</span>
                    <div className="visit-empty-state-title">
                      {translate(VISIT_PAGE, "surveys_no_results", me.language)}
                      {searchQuery && <span className="visit-empty-state-query"> «{searchQuery}»</span>}
                    </div>
                  </div>
                );
              }
              return filteredSurveys.map(survey => {
                const isExpanded = !!expandedCards[survey.id];
                const isDetailLoading = !!surveyDetailLoading[survey.id];
                const detail = surveyDetails[survey.id];
                const groups = detail?.groups || {};
                const products = groups.products || [];
                const brands = groups.brands || [];
                const categories = groups.categories || [];
                const other = groups.other || [];
                const groupCounts = { products: products.length, brands: brands.length, categories: categories.length, other: other.length };
                const tab = activeTabs[survey.id] || (detail ? TAB_CONFIG.find(t => groupCounts[t.key] > 0)?.key || "products" : "products");
                const entityQuery = (entitySearchDebounced[survey.id] || "").toLowerCase();

                const isEmpty = detail && !isDetailLoading && products.length === 0 && brands.length === 0 && categories.length === 0 && other.length === 0;
                if (isEmpty) return null;

                const filteredProducts = entityQuery
                  ? products.filter(p => (p.product?.name || "").toLowerCase().includes(entityQuery) || (p.product?.code || "").toLowerCase().includes(entityQuery))
                  : products;
                const filteredBrands = entityQuery ? brands.filter(b => (b.brand?.name || "").toLowerCase().includes(entityQuery)) : brands;
                const filteredCategories = entityQuery ? categories.filter(c => (c.category?.name || "").toLowerCase().includes(entityQuery)) : categories;
                const filteredOther = entityQuery ? other.filter(a => (a.question?.text || "").toLowerCase().includes(entityQuery)) : other;

                const tabs = TAB_CONFIG.map(t => ({
                  ...t,
                  count:
                    t.key === "products"
                      ? filteredProducts.length
                      : t.key === "brands"
                      ? filteredBrands.length
                      : t.key === "categories"
                      ? filteredCategories.length
                      : filteredOther.length,
                }));

                return (
                  <div key={survey.id} className={`visit-survey-card${isExpanded ? " expanded" : ""}`}>
                    <div className="visit-survey-card-header" onClick={() => toggleCard(survey.id)}>
                      <div className="visit-survey-icon">
                        <span className="material-symbols-outlined">assignment</span>
                      </div>
                      <div className="visit-survey-info">
                        <div className="visit-survey-title">{survey.title}</div>
                        <div className="visit-survey-meta">
                          <span className="visit-badge visit-badge-completed">
                            <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>
                              check
                            </span>
                            {survey.answers_count}
                          </span>
                          <span className="visit-badge visit-badge-photos">
                            <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>
                              photo
                            </span>
                            {survey.photos_count}
                          </span>
                          <div className="visit-survey-meta-item">
                            <span className="material-symbols-outlined">person</span>
                            {(data.user?.first_name || "") + " " + (data.user?.last_name || "")}
                          </div>
                        </div>
                      </div>
                      <div className="visit-survey-chevron">
                        <span className="material-symbols-outlined">expand_more</span>
                      </div>
                    </div>

                    <div className="visit-survey-detail">
                      {isDetailLoading ? (
                        <Loading loading />
                      ) : (
                        <>
                          <div className="visit-inner-tabs">
                            {tabs.map(t => (
                              <button key={t.key} className={`visit-inner-tab${tab === t.key ? " active" : ""}`} onClick={e => switchTab(survey.id, t.key, e)}>
                                <span className="material-symbols-outlined">{t.icon}</span>
                                {translate(VISIT_PAGE, t.labelKey, me.language)}
                                <span className="visit-itc">{t.count}</span>
                              </button>
                            ))}
                            <div className="visit-inner-search" onClick={e => e.stopPropagation()}>
                              <span className="material-symbols-outlined">search</span>
                              <input
                                type="text"
                                placeholder={translate(VISIT_PAGE, "surveys_entity_search_placeholder", me.language)}
                                defaultValue=""
                                onChange={e => handleEntitySearch(survey.id, e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Products */}
                          <div className={`visit-inner-tab-panel${tab === "products" ? " active" : ""}`}>
                            {filteredProducts.length === 0 ? (
                              <div className="visit-empty-state">
                                <span className="material-symbols-outlined">inventory_2</span>
                                <div className="visit-empty-state-title">{translate(VISIT_PAGE, "surveys_empty_products", me.language)}</div>
                              </div>
                            ) : (
                              <>
                                {filteredProducts.slice(0, getLimit(survey.id, "products")).map(prod => {
                                  const entityId = `${survey.id}-p${prod.product.id}`;
                                  const isCollapsed = !collapsedEntities[entityId];
                                  const photoCount = prod.answers.reduce((sum, a) => sum + (a.photos?.length || 0), 0);
                                  return (
                                    <div key={entityId} className={`visit-entity-item${isCollapsed ? " collapsed" : ""}`}>
                                      <div className="visit-entity-row" onClick={e => toggleEntity(entityId, e)}>
                                        <span className="material-symbols-outlined visit-chevron">expand_more</span>
                                        <div
                                          className="visit-entity-icon-sm"
                                          style={{ background: "var(--md-primary-container)", color: "var(--md-on-primary-container)" }}>
                                          <span className="material-symbols-outlined">inventory_2</span>
                                        </div>
                                        <span className="visit-entity-name">{prod.product.name}</span>
                                        <span className="visit-entity-sub">{prod.product.code}</span>
                                        <span className="visit-entity-answer-count">
                                          <span className="material-symbols-outlined">question_answer</span>
                                          {prod.answers.length}
                                        </span>
                                        {photoCount > 0 && (
                                          <span className="visit-entity-photo-count">
                                            <span className="material-symbols-outlined">photo_camera</span>
                                            {photoCount}
                                          </span>
                                        )}
                                      </div>
                                      <div className="visit-entity-answers">
                                        <table className="visit-qa-table">
                                          <tbody>
                                            {prod.answers.map(answer => {
                                              const meta = ANSWER_TYPE_META[answer.question?.answer_type];
                                              return (
                                                <tr key={answer.id}>
                                                  <td>
                                                    <span className="visit-q-text">{answer.question?.text}</span>
                                                    {meta && (
                                                      <span className="visit-q-type">
                                                        <span className="material-symbols-outlined">{meta.icon}</span>
                                                        {translate(VISIT_PAGE, meta.labelKey, me.language)}
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td>{renderAnswerCell(answer, openPhotoModal)}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  );
                                })}
                                {filteredProducts.length > getLimit(survey.id, "products") && (
                                  <div className="visit-show-more-row">
                                    <button
                                      className="visit-btn-ghost"
                                      onClick={e => {
                                        e.stopPropagation();
                                        showMore(survey.id, "products");
                                      }}>
                                      <span className="material-symbols-outlined">expand_more</span>
                                      {translate(VISIT_PAGE, "surveys_show_more", me.language)} {filteredProducts.length - getLimit(survey.id, "products")}
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Brands */}
                          <div className={`visit-inner-tab-panel${tab === "brands" ? " active" : ""}`}>
                            {filteredBrands.length === 0 ? (
                              <div className="visit-empty-state">
                                <span className="material-symbols-outlined">label_off</span>
                                <div className="visit-empty-state-title">{translate(VISIT_PAGE, "surveys_empty_brands", me.language)}</div>
                              </div>
                            ) : (
                              <>
                                {filteredBrands.slice(0, getLimit(survey.id, "brands")).map(item => {
                                  const entityId = `${survey.id}-b${item.brand.id}`;
                                  const isCollapsed = !collapsedEntities[entityId];
                                  const photoCount = item.answers.reduce((sum, a) => sum + (a.photos?.length || 0), 0);
                                  return (
                                    <div key={entityId} className={`visit-entity-item${isCollapsed ? " collapsed" : ""}`}>
                                      <div className="visit-entity-row" onClick={e => toggleEntity(entityId, e)}>
                                        <span className="material-symbols-outlined visit-chevron">expand_more</span>
                                        <div className="visit-entity-icon-sm" style={{ background: "#FFF3E0", color: "#E65100" }}>
                                          <span className="material-symbols-outlined">label</span>
                                        </div>
                                        <span className="visit-entity-name">{item.brand.name}</span>
                                        <span className="visit-entity-answer-count">
                                          <span className="material-symbols-outlined">question_answer</span>
                                          {item.answers.length}
                                        </span>
                                        {photoCount > 0 && (
                                          <span className="visit-entity-photo-count">
                                            <span className="material-symbols-outlined">photo_camera</span>
                                            {photoCount}
                                          </span>
                                        )}
                                      </div>
                                      <div className="visit-entity-answers">
                                        <table className="visit-qa-table">
                                          <tbody>
                                            {item.answers.map(answer => {
                                              const meta = ANSWER_TYPE_META[answer.question?.answer_type];
                                              return (
                                                <tr key={answer.id}>
                                                  <td>
                                                    <span className="visit-q-text">{answer.question?.text}</span>
                                                    {meta && (
                                                      <span className="visit-q-type">
                                                        <span className="material-symbols-outlined">{meta.icon}</span>
                                                        {translate(VISIT_PAGE, meta.labelKey, me.language)}
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td>{renderAnswerCell(answer, openPhotoModal)}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  );
                                })}
                                {filteredBrands.length > getLimit(survey.id, "brands") && (
                                  <div className="visit-show-more-row">
                                    <button
                                      className="visit-btn-ghost"
                                      onClick={e => {
                                        e.stopPropagation();
                                        showMore(survey.id, "brands");
                                      }}>
                                      <span className="material-symbols-outlined">expand_more</span>
                                      {translate(VISIT_PAGE, "surveys_show_more", me.language)} {filteredBrands.length - getLimit(survey.id, "brands")}
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Categories */}
                          <div className={`visit-inner-tab-panel${tab === "categories" ? " active" : ""}`}>
                            {filteredCategories.length === 0 ? (
                              <div className="visit-empty-state">
                                <span className="material-symbols-outlined">category</span>
                                <div className="visit-empty-state-title">{translate(VISIT_PAGE, "surveys_empty_categories", me.language)}</div>
                              </div>
                            ) : (
                              <>
                                {filteredCategories.slice(0, getLimit(survey.id, "categories")).map(item => {
                                  const entityId = `${survey.id}-c${item.category.id}`;
                                  const isCollapsed = !collapsedEntities[entityId];
                                  return (
                                    <div key={entityId} className={`visit-entity-item${isCollapsed ? " collapsed" : ""}`}>
                                      <div className="visit-entity-row" onClick={e => toggleEntity(entityId, e)}>
                                        <span className="material-symbols-outlined visit-chevron">expand_more</span>
                                        <div className="visit-entity-icon-sm" style={{ background: "#E8F5E9", color: "#2E7D32" }}>
                                          <span className="material-symbols-outlined">category</span>
                                        </div>
                                        <span className="visit-entity-name">{item.category.name}</span>
                                        <span className="visit-entity-answer-count">
                                          <span className="material-symbols-outlined">question_answer</span>
                                          {item.answers.length}
                                        </span>
                                      </div>
                                      <div className="visit-entity-answers">
                                        <table className="visit-qa-table">
                                          <tbody>
                                            {item.answers.map(answer => {
                                              const meta = ANSWER_TYPE_META[answer.question?.answer_type];
                                              return (
                                                <tr key={answer.id}>
                                                  <td>
                                                    <span className="visit-q-text">{answer.question?.text}</span>
                                                    {meta && (
                                                      <span className="visit-q-type">
                                                        <span className="material-symbols-outlined">{meta.icon}</span>
                                                        {translate(VISIT_PAGE, meta.labelKey, me.language)}
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td>{renderAnswerCell(answer, openPhotoModal)}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  );
                                })}
                                {filteredCategories.length > getLimit(survey.id, "categories") && (
                                  <div className="visit-show-more-row">
                                    <button
                                      className="visit-btn-ghost"
                                      onClick={e => {
                                        e.stopPropagation();
                                        showMore(survey.id, "categories");
                                      }}>
                                      <span className="material-symbols-outlined">expand_more</span>
                                      {translate(VISIT_PAGE, "surveys_show_more", me.language)} {filteredCategories.length - getLimit(survey.id, "categories")}
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Other */}
                          <div className={`visit-inner-tab-panel${tab === "other" ? " active" : ""}`}>
                            {filteredOther.length === 0 ? (
                              <div className="visit-empty-state">
                                <span className="material-symbols-outlined">help_outline</span>
                                <div className="visit-empty-state-title">{translate(VISIT_PAGE, "surveys_empty_other", me.language)}</div>
                              </div>
                            ) : (
                              <>
                                <table className="visit-other-qa-table">
                                  <tbody>
                                    {filteredOther.slice(0, getLimit(survey.id, "other")).map(answer => {
                                      const meta = ANSWER_TYPE_META[answer.question?.answer_type];
                                      return (
                                        <tr key={answer.id}>
                                          <td>
                                            <span className="visit-q-text">{answer.question?.text}</span>
                                            {meta && (
                                              <span className="visit-q-type">
                                                <span className="material-symbols-outlined">{meta.icon}</span>
                                                {translate(VISIT_PAGE, meta.labelKey, me.language)}
                                              </span>
                                            )}
                                          </td>
                                          <td>{renderAnswerCell(answer, openPhotoModal)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                {filteredOther.length > getLimit(survey.id, "other") && (
                                  <div className="visit-show-more-row">
                                    <button
                                      className="visit-btn-ghost"
                                      onClick={e => {
                                        e.stopPropagation();
                                        showMore(survey.id, "other");
                                      }}>
                                      <span className="material-symbols-outlined">expand_more</span>
                                      {translate(VISIT_PAGE, "surveys_show_more", me.language)} {filteredOther.length - getLimit(survey.id, "other")}
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </>
      ) : (
        <Loading loading={loading} />
      )}

      {photoModal && (
        <div className="visit-photo-modal-backdrop" onClick={closePhotoModal}>
          <div className="visit-photo-modal-content" onClick={e => e.stopPropagation()}>
            <div className="visit-photo-modal-img">{photoModal.photos[photoModal.index] && <img src={photoModal.photos[photoModal.index].photo} alt="" />}</div>
            {photoModal.photos.length > 1 && (
              <div className="visit-photo-modal-nav-row">
                <button className="visit-photo-modal-nav" onClick={prevPhoto} disabled={photoModal.index === 0}>
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <span className="visit-photo-modal-counter">
                  {photoModal.index + 1} / {photoModal.photos.length}
                </span>
                <button className="visit-photo-modal-nav" onClick={nextPhoto} disabled={photoModal.index === photoModal.photos.length - 1}>
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            )}
            {photoModal.question && <div className="visit-photo-modal-question">{photoModal.question}</div>}
            <button className="visit-photo-modal-close" onClick={closePhotoModal}>
              {translate(VISIT_PAGE, "surveys_modal_close", me.language)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Surveys;